import { isEqual } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../../actions';
import {
    CHANGE_GOALS_CHECKED_FROM_COLUMNS_SETTINGS_EDITOR,
    changeColumnsCheckedFromColumnsSettingsEditor,
    ChangeGoalsCheckedFromColumnsSettingsEditor,
    OPEN_COLUMNS_SETTINGS_EDITOR,
    OpenColumnsSettingsEditor,
    SAVE_COLUMNS_SETTINGS_EDITOR,
    SaveColumnsSettingsEditor,
    updateGoalsSettingsColumnsSettingsEditor
} from '../../actions/columnsSettingsEditor';
import { updateColumnsSettingsDealsState } from '../../actions/deals';
import { updateGoalsData } from '../../actions/goals';
import {
    resetGridData,
    updateColumnsSettingsGridState,
    updateCommonSettingsGridState,
    updateGoalsSettingsGridState
} from '../../actions/grid-screen';
import { updateColumnsSettingsLogState } from '../../actions/log';
import { updateColumnsSettingsPricePackagesState } from '../../actions/pricePackages';
import { updateColumnsSettingsReportsState } from '../../actions/reportsClient';
import { updateColumnsSettingsReportsDomainState } from '../../actions/reportsDomain';
import { GroupedColumnKey } from '../../declarations/goals/goals';
import { selectColumnsSettings } from '../../selectors/grid/grid';
import { getGoalsSettingsForColumnsSettingsEditor } from '../../service/DeclarationHelper';
import { Session } from '../../service/Session/Session';
import { LocalStorage, StorageKeyType } from '../../service/Storage/Storage';
import { EpicDeps } from '../../service/StoreFactory';
import {
    banners,
    campaigns,
    ColumnsSettingsViewDict,
    CommonSettingEditorDiff,
    groups,
    phrases
} from '../../typings/common';
import { GridRow } from '../../typings/domain';
import { GoalDecl } from '../../typings/goals';
import { GoalsQuery } from '../../typings/graphql-grid';
import { GridColumnDecl } from '../../typings/grid';
import { State } from '../../typings/state';

const columnsSettingsEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_COLUMNS_SETTINGS_EDITOR),
        mergeMap((action: SaveColumnsSettingsEditor): Action[] => {
            const storeData = store$.value;
            const {
                columnsSettingsEditor: { columnsSettings, goalsSettings = [], commonSettings },
                grid,
                commonData: { login }
            } = storeData;

            const isNeedUpdateGrid =
                !isEqual(goalsSettings, grid.goalsSettings) ||
                commonSettings.isOnlyFavoriteCampaignsVisible !== grid.isOnlyFavoriteCampaignsVisible;

            updateLocalStorage(commonSettings, columnsSettings, goalsSettings, login);

            const resultActions: Action[] = [
                updateCommonSettingsGridState(commonSettings),
                updateGoalsSettingsGridState(goalsSettings),
                updateColumnsSettingsGridState(columnsSettings),
                updateColumnsSettingsReportsState(columnsSettings),
                updateColumnsSettingsReportsDomainState(columnsSettings),
                updateColumnsSettingsLogState(columnsSettings),
                updateColumnsSettingsDealsState(columnsSettings),
                updateColumnsSettingsPricePackagesState(columnsSettings)
            ];

            if (isNeedUpdateGrid) {
                resultActions.push(resetGridData());
            }

            return resultActions;
        })
    );
};

const updateLocalStorage = (
    diff: CommonSettingEditorDiff,
    columnsSettings: ColumnsSettingsViewDict,
    goalsSettings: GoalDecl[],
    login: string
) => {
    LocalStorage.setItemsFromObj(diff);
    LocalStorage.setItem(StorageKeyType.columnsSettings, columnsSettings);
    Session.setValueToCurrentSession(login, {
        [StorageKeyType.goalsSettings]: goalsSettings
    });
};

const openColumnsSettingsEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_COLUMNS_SETTINGS_EDITOR),
        mergeMap(
            (action: OpenColumnsSettingsEditor): Observable<Action> => {
                const state = store$.value;
                const {
                    commonData: { login },
                    grid: {
                        selectionInfo: { campaigns: campaignIdIn }
                    }
                } = state;

                return from(deps.goalsDataProvider.fetchMetricsGoals(login, campaignIdIn)).pipe(
                    mergeMap((response: GoalsQuery): Observable<Action> | Array<Action> => {
                        const {
                            client: { campaignGoals }
                        } = response;

                        const goalsMap = campaignGoals.rowset.reduce((acc, goal) => {
                            acc[String(goal.id)] = {
                                id: String(goal.id),
                                name: goal.name
                            };

                            return acc;
                        }, {});

                        let actionsList: Action[] = [updateGoalsData(goalsMap)];

                        const { goalsSettings } = Session.getValueFromCurrentLocalSession(login);

                        const goalsSettingsColumnsSettingsEditor = getGoalsSettingsForColumnsSettingsEditor(
                            goalsMap,
                            goalsSettings
                        );

                        if (goalsSettingsColumnsSettingsEditor) {
                            actionsList = actionsList.concat([
                                updateGoalsSettingsColumnsSettingsEditor(goalsSettingsColumnsSettingsEditor)
                            ]);
                        }

                        return actionsList;
                    }),
                    catchError((): Observable<Action> => EMPTY)
                );
            }
        )
    );
};

const changeGoalsCheckedFromColumnsSettingsEditorEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(CHANGE_GOALS_CHECKED_FROM_COLUMNS_SETTINGS_EDITOR),
        mergeMap((action: ChangeGoalsCheckedFromColumnsSettingsEditor): Action[] => {
            const storeData = store$.value;

            const columnsMap = selectColumnsSettings(storeData);
            const hasSelectedGoalsAtColumnsSettingsEditor = action.listKeys.length > 0;

            return Object.keys(columnsMap).reduce((resultActions: Action[], viewKey) => {
                const currentColumns: Array<GridColumnDecl<GridRow>> = columnsMap[viewKey];

                if ([campaigns, groups, banners, phrases].includes(viewKey)) {
                    resultActions.push(
                        changeColumnsCheckedFromColumnsSettingsEditor(
                            viewKey,
                            currentColumns.reduce((acc: string[], column) => {
                                if (column.key === GroupedColumnKey.GOALS && hasSelectedGoalsAtColumnsSettingsEditor) {
                                    acc.push(column.key);
                                } else if (column.key !== GroupedColumnKey.GOALS && !column.hidden) {
                                    acc.push(column.key);
                                }
                                return acc;
                            }, [])
                        )
                    );
                }

                return resultActions;
            }, []);
        })
    );
};

export { columnsSettingsEditorEpic, changeGoalsCheckedFromColumnsSettingsEditorEpic, openColumnsSettingsEditorEpic };
